import { ANETCode } from "./errorMap";

export interface AcceptResponse {
  messages: {
    resultCode: string;
    message: Array<{
      code: ANETCode;
      text: string;
    }>;
  };
  opaqueData?: {
    dataDescriptor: string;
    dataValue: string;
  };
}

/** The card, plus the public keys that authorize tokenizing it. */
export interface AcceptSecureData {
  authData: {
    clientKey: string;
    apiLoginID: string;
  };
  cardData: {
    cardNumber: string;
    month: string;
    year: string;
    cardCode: string;
  };
}

/**
 * `window.Accept` as this module uses it. Accept.js ships no types and installs
 * itself on the global, so every access to it is a cast — which is exactly why
 * they all live in this file and nothing outside it touches the global.
 */
type AcceptGlobal = {
  isReady?: boolean;
  Accept?: {
    dispatchData: (
      secureData: AcceptSecureData,
      callback: (response: AcceptResponse) => void
    ) => void;
  };
};

const acceptGlobal = () => window as unknown as AcceptGlobal;

// Accept.js loads in two stages: accept.js parses immediately and installs
// a fail-stub `window.Accept.dispatchData`; the inner acceptCore.js loads
// after, completes a handshake, replaces the stub with the real impl, and
// sets `window.isReady = true`. If we call `dispatchData` before that, it
// hits the stub and tokenization silently fails — which manifests as "The
// transaction was unsuccessful" the next time the user submits.
//
// `loadAuthorizeAccept` therefore resolves only once `window.isReady` is
// true. Callers should invoke it on mount (to warm the script up) and
// again right before `dispatchData` (to block on readiness).
const ACCEPT_READY_TIMEOUT_MS = 15_000;
const ACCEPT_READY_POLL_MS = 50;

const waitForAcceptReady = (): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const deadline = Date.now() + ACCEPT_READY_TIMEOUT_MS;
    const poll = () => {
      if (acceptGlobal().isReady === true) {
        resolve();
        return;
      }
      if (Date.now() > deadline) {
        reject(new Error("Accept.js handshake timed out"));
        return;
      }
      setTimeout(poll, ACCEPT_READY_POLL_MS);
    };
    poll();
  });

export const loadAuthorizeAccept = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById("accept-js");

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = import.meta.env.VITE_ANET_ACCEPTJS_URL;
      script.id = "accept-js";
      script.type = "text/javascript";
      script.charset = "utf-8";
      script.async = true;
      script.onload = () => {
        waitForAcceptReady().then(resolve, reject);
      };
      script.onerror = () =>
        reject(new Error("Failed to load the Accept.js script."));
      document.head.appendChild(script);
    } else {
      waitForAcceptReady().then(resolve, reject);
    }
  });
};

/**
 * Hand the card to Accept.js and get a token back.
 *
 * Blocks on readiness first: calling `dispatchData` before the handshake
 * completes hits the fail-stub described above and fails silently.
 */
export const dispatchCardData = async (
  secureData: AcceptSecureData
): Promise<AcceptResponse> => {
  await loadAuthorizeAccept();
  const accept = acceptGlobal().Accept;
  if (!accept) {
    throw new Error("Accept.js loaded without installing window.Accept.");
  }
  return await new Promise<AcceptResponse>((resolve) =>
    accept.dispatchData(secureData, resolve)
  );
};
